import styled from '@emotion/styled'
import { colors } from '../../__styling/settings/colors'
import { metrics, ThemeMargins } from '../../__styling/settings/metrics'
import { zIndex } from '../../__styling/settings/z-index'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { themedValue } from '../../__styling/themed-value'
import { breakpoint } from '../../__styling'

export interface GridProps {
    verticalSpacing: keyof ThemeMargins | undefined
    stickyOffset?: number
    isTwoColumnOnTablet?: boolean
    compact?: boolean
}

export const StyledJuliet = styled('div')<GridProps>(
    {
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
    },
    ({
        stickyOffset,
        verticalSpacing,
        isTwoColumnOnTablet,
        theme,
        compact = false,
    }) => [
        {
            marginBottom:
                verticalSpacing &&
                calcRem(metrics[theme.kind].margins[verticalSpacing]),
        },
        stickyOffset !== undefined && {
            position: 'sticky',
            top: `${stickyOffset}px`,
        },
        theme.kind === 'thenightly' &&
            isTwoColumnOnTablet && {
                [breakpoint('sm')]: {
                    columnGap: calcRem(metrics.thenightly.margins.md),
                },
                [breakpoint('lg')]: {
                    columnGap: 0,
                },
            },
        compact && {
            marginBottom: 0,
        },
    ],
)

export const StyledHeader = styled('div')(
    {
        display: 'block',
        width: '100%',
    },
    ({ theme }) => ({
        marginBottom:
            theme.kind === 'thenightly'
                ? calcRem(metrics[theme.kind].margins.sm)
                : calcRem(metrics[theme.kind].margins.md),

        [breakpoint('lg')]: {
            marginBottom:
                theme.kind === 'thenightly'
                    ? 0
                    : calcRem(metrics[theme.kind].margins.md),
        },
    }),
)

export const StyledGridItem = styled('div')<{
    noHorizontalDividers?: boolean
    hideLastDivider?: boolean
    isTwoColumnOnTablet?: boolean
}>(
    {
        display: 'flex',
        justifyContent: 'flex-start',
        flexGrow: 1,
        flexShrink: 1,
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch',
        flex: '0 0 auto',
        position: 'relative',
    },
    ({
        theme,
        noHorizontalDividers,
        hideLastDivider,
        isTwoColumnOnTablet,
    }) => ({
        marginBottom:
            theme.kind === 'thenightly'
                ? 0
                : calcRem(metrics[theme.kind].margins.sm),
        paddingTop:
            theme.kind === 'thenightly'
                ? calcRem(metrics.thenightly.margins.md)
                : undefined,
        paddingBottom:
            theme.kind === 'thenightly'
                ? calcRem(metrics.thenightly.margins.md)
                : undefined,

        [breakpoint('sm')]: {
            flex:
                theme.kind === 'thenightly' && isTwoColumnOnTablet
                    ? `0 0 calc(50% - ${calcRem(
                          metrics.thenightly.margins.md / 2,
                      )})`
                    : '0 0 auto',
        },

        [breakpoint('lg')]: {
            flex: '0 0 auto',
        },

        '&:last-child': {
            marginBottom: '0',
            paddingBottom: theme.kind === 'thenightly' ? 0 : undefined,

            [breakpoint('lg')]: {
                paddingBottom:
                    theme.kind === 'thenightly'
                        ? hideLastDivider
                            ? 0
                            : calcRem(metrics[theme.kind].margins.xxxl)
                        : undefined,
            },
        },

        // Adds horizontal dividers
        '&::before': {
            content: themedValue(theme, {
                thewest: `''`,
                perthnow: `''`,
                thenightly: `''`,
                sevennews: undefined,
                fallback: undefined,
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyDolphin,
                perthnow: noHorizontalDividers
                    ? undefined
                    : colors.perthnow.greyMarble,
                thenightly: noHorizontalDividers
                    ? undefined
                    : theme.colors.borders.secondary,
                sevennews: undefined,
                fallback: undefined,
            }),
            width: '100%',
            height: noHorizontalDividers
                ? 0
                : theme.kind === 'thenightly'
                ? 0.5
                : 1,
            position: 'absolute',
            left: 0,
            bottom: 0,
            zIndex: zIndex.common.collectionDividers,
        },

        // only have horizontal divider on last card on perthnow & thenightly
        '&:last-child::before': {
            content: themedValue(theme, {
                thewest: 'none',
                perthnow: `''`,
                thenightly: `''`,
                sevennews: undefined,
                fallback: undefined,
            }),
            height: theme.kind === 'thenightly' ? 0 : 1,

            [breakpoint('lg')]: {
                height:
                    theme.kind === 'thenightly'
                        ? hideLastDivider
                            ? 0
                            : 0.5
                        : 1,
            },
        },

        ...(theme.kind === 'thenightly' &&
            isTwoColumnOnTablet && {
                // remove divider for last two items in two column layout
                '&:nth-last-child(-n+2)::before': {
                    [breakpoint('sm')]: {
                        height: 0,
                    },

                    [breakpoint('lg')]: {
                        height: 0.5,
                    },
                },
            }),

        '> .PortraitCard': {
            alignItems: 'flex-start',
        },
    }),
)

export const StyledInterlacedAd = styled.li(({ theme }) => {
    return {
        listStyle: 'none',
        width: '100%',

        ...(theme.kind === 'thenightly' && {
            margin: `${calcRem(metrics.thenightly.margins.xxl)} auto`,

            [breakpoint('sm')]: {
                margin: `${calcRem(metrics.thenightly.margins.lg)} auto`,
            },

            [breakpoint('lg')]: {
                margin: `${calcRem(metrics.thenightly.margins.xxxl)} auto`,
            },
        }),
    }
})
